import uniq from 'lodash.uniq';
import * as React from 'react';
import { noop } from 'vet-bones/utils';

import { exportProject } from 'src/actions/projects/action-creators/exportProject';
import { publishProject } from 'src/actions/projects/action-creators/publishProject/publishProject';
import {
  generateFileUploadNamedGraph,
  generateModelNamedGraph,
  generateVirtualGraphName,
} from 'src/containers/ProjectDiagram/utils/generateProjectIri';
import {
  ConnectionInfo,
  CreationType,
  CsvMappingInfo,
  CsvMappingsInfo,
  ModelInfo,
  RulesInfo,
  SensitivePropertyGroupsInfo,
  VirtualGraphMappingInfo,
  VirtualGraphMappingsInfo,
} from 'src/containers/PublishProjectDrawer/types';
import { connectionSelector } from 'src/selectors/connection';
import { databaseSensitivePropertyGroupsSelector } from 'src/selectors/databaseSensitivePropertyGroups';
import { modelItemsSelector } from 'src/selectors/modelItems';
import { namedGraphItemsSelector } from 'src/selectors/namedGraphItems';
import { projectSelector } from 'src/selectors/project';
import { projectSensitivePropertyGroupsByNameSelector } from 'src/selectors/projectSensitivePropertyGroupsByName';
import { virtualGraphItemsSelector } from 'src/selectors/virtualGraphItems';
import {
  ProjectFileUpload,
  ProjectVirtualGraph,
} from 'src/store/state/projects';
import * as copy from 'src/templates/copy';
import { useReduxDispatch } from 'src/utils/hooks/useReduxDispatch';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';
import { useRouterParams } from 'src/utils/hooks/useRouterParams';
import {
  INVALID_IRI_MATCHER,
  IRI_STARTS_WITH_MATCHER,
} from 'src/utils/removeInvalidIriChars';
import {
  INVALID_NAME_MATCHER,
  NAME_STARTS_WITH_MATCHER,
  replaceInvalidNameChars,
} from 'src/utils/removeInvalidNameChars';

// default info

export const useDefaultModelInfo = (databaseId: string) => {
  const { projectId } = useRouterParams();
  const project = useReduxSelector(projectSelector(projectId));
  const findModel = useFindModel(databaseId);
  return React.useCallback((): ModelInfo => {
    if (!project) {
      return {
        creationType: CreationType.NEW,
        modelName: '',
        namedGraphIri: '',
      };
    }
    const modelName =
      project.publishHistory.modelHistory.modelName ||
      replaceInvalidNameChars(project.name);
    const model = findModel(modelName);

    let namedGraphIri;
    let creationType;
    if (
      model &&
      (!project.publishHistory.modelHistory.namedGraphIri ||
        model.namedGraph === project.publishHistory.modelHistory.namedGraphIri)
    ) {
      creationType = CreationType.REPLACE;
      namedGraphIri = model.namedGraph || '';
    } else {
      creationType = CreationType.NEW;
      namedGraphIri =
        project.publishHistory.modelHistory.namedGraphIri ||
        generateModelNamedGraph(project);
    }

    return {
      creationType,
      modelName,
      namedGraphIri,
    };
  }, [findModel, project]);
};

export const useDefaultSPGInfo = (databaseId: string) => {
  const { projectId } = useRouterParams();
  const project = useReduxSelector(projectSelector(projectId));
  const sensitivePropertyGroupsByName = useReduxSelector(
    projectSensitivePropertyGroupsByNameSelector(projectId)
  );
  const databaseSensitivePropertyGroupsByName = useReduxSelector(
    databaseSensitivePropertyGroupsSelector(databaseId)
  );

  return React.useCallback(() => {
    const sensitivePropertyGroupsForPublish: SensitivePropertyGroupsInfo = {};

    Object.keys(sensitivePropertyGroupsByName).forEach((name) => {
      const attributes = sensitivePropertyGroupsByName[name];
      if (attributes.length < 1) {
        return;
      }
      const history =
        project?.publishHistory.sensitivePropertyGroupsByName[name];

      const databaseSpg =
        databaseSensitivePropertyGroupsByName[history?.publishName || name];
      let actionType: CreationType;
      if (databaseSpg) {
        if (!history?.actionType || history?.actionType === CreationType.NEW) {
          actionType = CreationType.APPEND;
        } else {
          actionType = history.actionType;
        }
      } else {
        actionType = CreationType.NEW;
      }
      sensitivePropertyGroupsForPublish[name] = {
        spgName: name,
        publishName: history?.publishName || name,
        actionType,
        attributes,
      };
    });
    return sensitivePropertyGroupsForPublish;
  }, [
    sensitivePropertyGroupsByName,
    project,
    databaseSensitivePropertyGroupsByName,
  ]);
};

export const useDefaultCsvMappingInfo = (databaseId: string) => {
  const { projectId } = useRouterParams();
  const project = useReduxSelector(projectSelector(projectId));
  const findNamedGraph = useFindNamedGraph(databaseId);
  return React.useCallback(
    (fileUpload: ProjectFileUpload): CsvMappingInfo => {
      const history =
        project?.publishHistory.csvResourceHistoriesById[fileUpload.id];

      const namedGraphIri =
        history?.namedGraphIri ||
        (project ? generateFileUploadNamedGraph(project, fileUpload) : '');
      let creationType: CreationType;
      if (findNamedGraph(namedGraphIri)) {
        if (
          !history?.creationType ||
          history.creationType === CreationType.NEW
        ) {
          creationType = CreationType.REPLACE;
        } else {
          creationType = history.creationType;
        }
      } else {
        creationType = CreationType.NEW;
      }
      return {
        creationType,
        fileOptions: null,
        namedGraphIri,
      };
    },
    [findNamedGraph, project]
  );
};

export const useDefaultVirtualGraphMappingInfo = (databaseId: string) => {
  const { projectId } = useRouterParams();
  const project = useReduxSelector(projectSelector(projectId));
  // Stardog does not allow updating the stardog DB for a VG after creation
  // so we must filter by virtual graphs that are visible for this database
  const findVirtualGraph = useFindVirtualGraph(databaseId);
  return React.useCallback(
    (virtualGraph: ProjectVirtualGraph): VirtualGraphMappingInfo => {
      if (!project) {
        return {
          creationType: CreationType.NEW,
          virtualGraphName: '',
        };
      }

      const virtualGraphName =
        project.publishHistory.vgResourceHistoriesById[virtualGraph.id]
          ?.virtualGraphName || generateVirtualGraphName(project, virtualGraph);
      const creationType = findVirtualGraph(virtualGraphName)
        ? CreationType.REPLACE
        : CreationType.NEW;
      return {
        creationType,
        virtualGraphName,
      };
    },
    [findVirtualGraph, project]
  );
};

// model

export const useFindModel = (databaseId: string) => {
  const modelItems = useReduxSelector(modelItemsSelector(databaseId));
  return React.useCallback(
    (modelName: string) => modelItems.find((item) => item.value === modelName),
    [modelItems]
  );
};

export const useModelNamePlaceholder = (databaseId: string) => {
  const findModel = useFindModel(databaseId);
  const { modelName } = useDefaultModelInfo(databaseId)();
  const modelNamePlaceholder = findModel(modelName) ? '' : modelName;
  return modelNamePlaceholder;
};

export const useValidateModelName = (databaseId: string) => {
  const findModel = useFindModel(databaseId);
  return React.useCallback(
    (name: string): string => {
      if (!name) {
        return copy.components.publishProjectDrawer.validation.modelName
          .required;
      }

      if (!name.match(NAME_STARTS_WITH_MATCHER)) {
        return copy.components.publishProjectDrawer.validation.modelName
          .startsWith;
      }

      const invalidChars = name.match(INVALID_NAME_MATCHER);
      if (invalidChars) {
        return copy.components.publishProjectDrawer.validation.modelName.characters(
          uniq(invalidChars)
        );
      }

      if (findModel(name)) {
        return copy.components.publishProjectDrawer.validation.modelName.unique;
      }

      return '';
    },
    [findModel]
  );
};

// named graphs

export const useFindNamedGraph = (databaseId: string) => {
  const namedGraphItems = useReduxSelector(namedGraphItemsSelector(databaseId));
  return React.useCallback(
    (iri: string) => namedGraphItems.find((item) => item.value === iri),
    [namedGraphItems]
  );
};

export const useModelNamedGraphPlaceholder = (databaseId: string) => {
  const findNamedGraph = useFindNamedGraph(databaseId);
  const { namedGraphIri } = useDefaultModelInfo(databaseId)();
  const namedGraphIriPlaceholder = findNamedGraph(namedGraphIri)
    ? ''
    : namedGraphIri;
  return namedGraphIriPlaceholder;
};

export const useCsvNamedGraphIriPlaceholder = (
  databaseId: string,
  fileUpload: ProjectFileUpload
) => {
  const findNamedGraph = useFindNamedGraph(databaseId);
  const { namedGraphIri } = useDefaultCsvMappingInfo(databaseId)(fileUpload);
  const namedGraphIriPlaceholder = findNamedGraph(namedGraphIri)
    ? ''
    : namedGraphIri;
  return namedGraphIriPlaceholder;
};

export const useValidateNamedGraphIri = (databaseId: string) => {
  const findNamedGraph = useFindNamedGraph(databaseId);
  return React.useCallback(
    (iri: string): string => {
      if (!iri) {
        return copy.components.publishProjectDrawer.validation.namedGraph
          .required;
      }

      if (!iri.match(IRI_STARTS_WITH_MATCHER)) {
        return copy.components.publishProjectDrawer.validation.namedGraph
          .startsWith;
      }

      if (!iri.includes(':')) {
        return copy.components.publishProjectDrawer.validation.namedGraph
          .semicolon;
      }

      const invalidChars = iri.match(INVALID_IRI_MATCHER);
      if (invalidChars) {
        return copy.components.publishProjectDrawer.validation.namedGraph.characters(
          uniq(invalidChars)
        );
      }

      if (findNamedGraph(iri)) {
        return copy.components.publishProjectDrawer.validation.namedGraph
          .unique;
      }

      return '';
    },
    [findNamedGraph]
  );
};

// virtual graphs

export const useFindVirtualGraph = (databaseId?: string) => {
  const virtualGraphItems = useReduxSelector(
    virtualGraphItemsSelector(databaseId)
  );
  return React.useCallback(
    (virtualGraphName: string) =>
      virtualGraphItems.find((item) => item.value === virtualGraphName),
    [virtualGraphItems]
  );
};

export const useVirtualGraphNamePlaceholder = (
  databaseId: string,
  virtualGraph: ProjectVirtualGraph
) => {
  const findVirtualGraph = useFindVirtualGraph();
  const { virtualGraphName } =
    useDefaultVirtualGraphMappingInfo(databaseId)(virtualGraph);
  const virtualGraphNamePlaceholder = findVirtualGraph(virtualGraphName)
    ? ''
    : virtualGraphName;
  return virtualGraphNamePlaceholder;
};

export const useValidateVirtualGraphName = () => {
  const findVirtualGraph = useFindVirtualGraph();
  return React.useCallback(
    (name: string): string => {
      if (!name) {
        return copy.components.publishProjectDrawer.validation.virtualGraph
          .required;
      }

      if (!name.match(NAME_STARTS_WITH_MATCHER)) {
        return copy.components.publishProjectDrawer.validation.virtualGraph
          .startsWith;
      }

      const invalidChars = name.match(INVALID_NAME_MATCHER);
      if (invalidChars) {
        return copy.components.publishProjectDrawer.validation.virtualGraph.characters(
          uniq(invalidChars)
        );
      }

      if (findVirtualGraph(name)) {
        return copy.components.publishProjectDrawer.validation.virtualGraph
          .unique;
      }

      return '';
    },
    [findVirtualGraph]
  );
};

// publish

export type PublishContextProps = {
  doExportProject: boolean;
  handleFinishedPublish: (date: number | null) => void;
  handlePendingPublish: (date: number | null) => void;
  isPending: boolean;
  setDoExportProject: React.Dispatch<React.SetStateAction<boolean>>;
};

export const PublishContext = React.createContext<PublishContextProps>({
  doExportProject: false,
  handleFinishedPublish: noop,
  handlePendingPublish: noop,
  isPending: false,
  setDoExportProject: noop,
});

export const usePublishContext = () => {
  return React.useContext(PublishContext);
};

export type PublishOptions = {
  projectId: string;
  connectionInfo: ConnectionInfo;
  isPublishingRules: boolean;
  isPublishingSPG: boolean;
  modelInfo: ModelInfo;
  ruleInfo: RulesInfo;
  spgInfo: SensitivePropertyGroupsInfo;
  csvMappingInfo?: CsvMappingsInfo;
  vgMappingInfo?: VirtualGraphMappingsInfo;
};

export const usePublish = () => {
  const dispatch = useReduxDispatch();
  const connection = useReduxSelector(connectionSelector);
  const { doExportProject, handleFinishedPublish, handlePendingPublish } =
    usePublishContext();

  return React.useCallback(
    async ({
      projectId,
      connectionInfo,
      isPublishingRules,
      isPublishingSPG,
      modelInfo,
      csvMappingInfo,
      vgMappingInfo,
      ruleInfo,
      spgInfo,
    }: PublishOptions) => {
      const publishedDate = Date.now();
      handlePendingPublish(publishedDate);

      const succeeded = await dispatch(
        publishProject({
          projectId,
          publishedDate,
          connectionInfo,
          doExportProject,
          isPublishingRules,
          isPublishingSPG,
          modelInfo,
          csvMappingInfo,
          vgMappingInfo,
          ruleInfo,
          spgInfo,
        })
      );

      if (succeeded) {
        handleFinishedPublish(publishedDate);
        if (doExportProject) {
          await dispatch(exportProject(projectId, connection));
        }
      } else {
        handlePendingPublish(null);
      }
    },
    [
      connection,
      dispatch,
      doExportProject,
      handleFinishedPublish,
      handlePendingPublish,
    ]
  );
};
